import * as React from "react";
import { Link, useLocation } from "react-router";

import { cn } from "@/lib/utils";

export type SidebarNavItem = {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  count?: number;
  alert?: number;
  /**
   * Treat as a strict-equality match instead of a prefix match. Use for
   * "/" / "/dashboard" so they don't light up on every subpath.
   */
  end?: boolean;
  /**
   * Path prefixes that should suppress this item's active state. Used
   * when a child route has its own sidebar entry — e.g. "/properties"
   * excludes "/properties/import" so the two don't highlight together
   * when the user is on the import page.
   */
  excludePaths?: string[];
};

type SidebarNavProps = {
  items: SidebarNavItem[];
  className?: string;
};

export function SidebarNav({ items, className }: SidebarNavProps) {
  const { pathname } = useLocation();
  return (
    <nav className={cn("flex flex-col gap-0.5", className)}>
      {items.map((item) => (
        <SidebarLink
          key={item.href}
          item={item}
          isActive={computeActive(item, pathname)}
        />
      ))}
    </nav>
  );
}

function SidebarLink({
  item,
  isActive,
}: {
  item: SidebarNavItem;
  isActive: boolean;
}) {
  return (
    <Link
      to={item.href}
      className={cn(
        "group flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[14px] font-medium transition-colors",
        isActive
          ? "bg-muted text-foreground"
          : "text-ink-soft hover:bg-muted/60 hover:text-foreground",
      )}
      aria-current={isActive ? "page" : undefined}
    >
      <item.icon
        className={cn(
          "size-[15px] shrink-0",
          isActive ? "text-foreground" : "text-ink-soft",
        )}
      />
      <span className="flex-1 truncate">{item.label}</span>
      {item.alert !== undefined && item.alert > 0 ? (
        <span className="inline-flex min-w-4 items-center justify-center rounded-[5px] bg-primary px-1 font-mono text-[11px] leading-none text-primary-foreground font-semibold">
          {item.alert}
        </span>
      ) : item.count !== undefined ? (
        <span className="font-mono text-[11px] text-ink-faint tabular-nums">
          {item.count}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Returns true when {@code pathname} should activate {@code item}. We
 * use a manual prefix match (rather than React Router's NavLink) so we
 * can subtract specific child routes via {@link SidebarNavItem.excludePaths}.
 */
function computeActive(item: SidebarNavItem, pathname: string): boolean {
  if (item.excludePaths?.some((prefix) => isPathMatch(pathname, prefix))) {
    return false;
  }
  if (item.end === true) {
    return pathname === item.href;
  }
  return isPathMatch(pathname, item.href);
}

/** Match {@code prefix} exactly or as a path segment prefix (`/foo` matches `/foo/bar`). */
function isPathMatch(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}
